import { IDENTITY_DOC_TYPES } from './types';
export function luhn(digits) {
    if (!/^\d+$/.test(digits))
        return false;
    let sum = 0;
    let double = false;
    for (let index = digits.length - 1; index >= 0; index -= 1) {
        let digit = digits.charCodeAt(index) - 48;
        if (double) {
            digit *= 2;
            if (digit > 9)
                digit -= 9;
        }
        sum += digit;
        double = !double;
    }
    return sum % 10 === 0;
}
export function nhsMod11(digits) {
    if (!/^\d{10}$/.test(digits))
        return false;
    let sum = 0;
    for (let index = 0; index < 9; index += 1)
        sum += (digits.charCodeAt(index) - 48) * (10 - index);
    const remainder = 11 - (sum % 11);
    const check = remainder === 11 ? 0 : remainder;
    if (check === 10)
        return false;
    return check === digits.charCodeAt(9) - 48;
}
const NIT_WEIGHTS = [3, 7, 13, 17, 19, 23, 29, 37, 41, 43, 47, 53, 59, 67, 71];
export function coNitCheckDigit(nit) {
    if (!/^\d{1,15}$/.test(nit))
        return null;
    let sum = 0;
    const reversed = nit.split('').reverse();
    for (let index = 0; index < reversed.length; index += 1)
        sum += Number(reversed[index]) * (NIT_WEIGHTS[index] ?? 0);
    const remainder = sum % 11;
    return remainder > 1 ? 11 - remainder : remainder;
}
export function coNitDv(value) {
    const cleaned = value.replace(/[\s.]/g, '');
    const match = /^(\d{6,15})-?(\d)$/.exec(cleaned);
    if (!match)
        return false;
    return coNitCheckDigit(match[1]) === Number(match[2]);
}
export function isIdentityDocType(value) {
    return typeof value === 'string' && IDENTITY_DOC_TYPES.includes(value);
}
export function normaliseIdentity(raw, mode) {
    const trimmed = raw.replace(/[\s\-./]/g, '');
    return mode === 'digits' ? trimmed : trimmed.toUpperCase();
}
export function documentRule(profile, idType) {
    return profile.identity.documents.find((document) => document.type === idType) ?? null;
}
export function detectIdType(profile, raw) {
    if (!profile.identity.autoDetect)
        return profile.identity.defaultType;
    const digits = raw.replace(/[\s-]/g, '');
    const match = profile.identity.documents.find((document) => document.type !== 'passport' &&
        document.type !== 'no_document' &&
        document.onPatternFail === 'refuse' &&
        !document.needsIssuingCountry &&
        new RegExp(document.pattern).test(normaliseIdentity(digits, document.normalise)));
    return match?.type ?? profile.identity.defaultType;
}
export function checkIdentity(profile, idType, raw, path = 'cnic') {
    const rule = documentRule(profile, idType);
    if (!rule) {
        return { ok: false, messageKey: 'identity.type_not_used_here', message: `That kind of document is not used in ${profile.name}` };
    }
    if (idType === 'no_document')
        return { ok: true, idType, value: null, warnings: [] };
    const value = normaliseIdentity(raw ?? '', rule.normalise);
    if (!value)
        return { ok: false, messageKey: 'identity.number_required', message: `Enter the ${rule.label} number` };
    const warnings = [];
    if (!new RegExp(rule.pattern).test(value)) {
        const message = `This does not look like a ${rule.label} number (for example ${rule.example})`;
        if (rule.onPatternFail === 'refuse')
            return { ok: false, messageKey: 'identity.pattern', message };
        warnings.push({ path, messageKey: 'identity.pattern', message });
    }
    const checksumOk = rule.checksum === 'luhn' ? luhn(value) : rule.checksum === 'nhs_mod11' ? nhsMod11(value) : true;
    if (!checksumOk) {
        const message = `The last digit of this ${rule.label} does not match the others; check it against the card`;
        if (rule.onChecksumFail === 'refuse')
            return { ok: false, messageKey: 'identity.checksum', message };
        warnings.push({ path, messageKey: 'identity.checksum', message });
    }
    return { ok: true, idType, value, warnings };
}
export function effectiveIdType(row, country) {
    if (isIdentityDocType(row.idType))
        return row.idType;
    const value = (row.cnic ?? '').replace(/[\s-]/g, '');
    if (!value)
        return null;
    if (country === 'SA') {
        if (/^1\d{9}$/.test(value) && luhn(value))
            return 'sa_nid';
        if (/^2\d{9}$/.test(value) && luhn(value))
            return 'sa_iqama';
        return 'legacy_other';
    }
    if ((country === 'PK' || country === null) && /^\d{13}$/.test(value))
        return 'pk_cnic';
    return 'legacy_other';
}
const NPHIES_SYSTEMS = {
    sa_nid: 'nationalId',
    sa_iqama: 'iqama',
    sa_border: 'borderNumber',
    sa_visa: 'visa',
    gcc_id: 'gccId',
    passport: 'passportNumber',
};
export function nphiesSystemFor(idType) {
    if (!idType || idType === 'legacy_other')
        return null;
    return NPHIES_SYSTEMS[idType] ?? null;
}
export function impliedNationality(profile, idType) {
    if (!idType)
        return null;
    return documentRule(profile, idType)?.impliesNationality ?? null;
}
//# sourceMappingURL=identity.js.map